import { computed, effect, inject, Injectable, linkedSignal, Signal, signal } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { TenantFacade } from '@core/data/tenant/tenant-facade';
import { AuthFacade } from '@core/auth';
import { HttpErrorMapper } from '@core/http/errors/http-error.mapper';
import {
  codeToDomainError,
  DomainError,
  DomainErrorKind,
  resolveKnownErrorKind,
  SsoTenantNotFoundError,
} from '@core/http/errors/domain-errors';
import { LoginCredentials } from '@core/auth';
import { HttpErrorResponse } from '@angular/common/http';
import { finalize } from 'rxjs/operators';
import { catchError, EMPTY, map, Observable, switchMap, tap } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';
import { AuthStep, SignInViewModel } from '@features/auth/application/sign-in.model';

/**
 * SignInFacade
 *
 * Orchestrates sign-in flow:
 * 1. Validate email format
 * 2. Load tenant configuration
 * 3. Authentication (mocked)
 * 4. Navigate to dashboard
 */
@Injectable()
export class SignInFacade {
  private readonly tenantFacade = inject(TenantFacade);
  private readonly authFacade = inject(AuthFacade);
  private readonly errorMapper = inject(HttpErrorMapper);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);

  protected queryParams = toSignal(this.route.queryParams, { initialValue: {} as Params });

  private readonly _error = linkedSignal<DomainError|null>(() => {
    const errorCode = this.queryParams()['error'];
    if (!errorCode) {
      return null;
    }
    return codeToDomainError(errorCode);
  })
  private readonly _errorMessage = linkedSignal<string | null>(() => {
    const domainError = this._error();
    return domainError ? this.resolveSafeErrorMessage(domainError) : null
  });

  private readonly _returnUrl = linkedSignal<string | null>(() => this.queryParams()['returnUrl']);
  private readonly _authStep = linkedSignal<DomainError|null, AuthStep>({
    source: () => this._error(),
    computation: (next, prev): AuthStep => {
      const errorMessage = next;
      if (errorMessage) {
        if (errorMessage instanceof SsoTenantNotFoundError) {
          return 'sso';
        }
      }

      if (prev?.value) {
        return prev.value;
      }
      return 'select';
    }
  })

  private readonly _isSubmitting = signal(false);

  public readonly returnUrl = this._returnUrl.asReadonly();

  readonly vm: Signal<SignInViewModel> = computed(() => {
    const tenantConfig = this.tenantFacade.tenantVm();
    return {
      isSubmitting: this._isSubmitting(),
      errorMessage: this._errorMessage(),
      tenantLoading: this.tenantFacade.loading() && !tenantConfig,
      authStep: this._authStep(),
    };
  });

  /**
   * Submit email and initiate sign-in flow.
   * If returnUrl is provided and is a valid relative path, the user will be
   * navigated there after successful authentication instead of /dashboard.
   */
  submitAuthentication(credentials: LoginCredentials, returnUrl?: string | null): void {
    this._errorMessage.set(null);
    this._isSubmitting.set(true);

    this.authenticate(credentials)
      .pipe(
        tap(() => {
          void this.router.navigate([this.resolvePostLoginRoute(returnUrl)]);
        }),
        catchError((error) => {
          const message = this.resolveSafeErrorMessage(error);
          this._errorMessage.set(message);

          return EMPTY;
        }),
        finalize(() => {
          this._isSubmitting.set(false);
        })
      )
      .subscribe();
  }

  submitSsoAuthentication(credentials: LoginCredentials): void {
    this._errorMessage.set(null);
    this._isSubmitting.set(true);

    const domain = credentials.username.split('@')[1];

    if (domain) {
      this.authFacade.loginWithSSO(domain);
    }
  }

  /**
   * Clear error when user starts editing
   */
  clearError(): void {
    this._errorMessage.set(null);
  }

  authenticate(credentials: LoginCredentials): Observable<void> {
    return this.authFacade.login(credentials).pipe(
      switchMap(() => this.tenantFacade.waitForAccessibleTenants()),
      tap((tenants) => {
        if (tenants.length === 0) {
          throw new HttpErrorResponse({
            status: 403,
            statusText: 'Unauthorized',
            error: 'No Accessible Tenant Found',
          });
        }
      }),
      map(() => undefined)
    );
  }

  private resolveSafeErrorMessage(error: unknown): string {
    return this.errorMapper.toUserMessage(error);
  }

  /**
   * Validate and resolve the post-login navigation target.
   *
   * Only accepts relative paths that start with '/' and do not start with '//'
   * (protocol-relative URLs that could redirect to an attacker-controlled host).
   * Falls back to '/dashboard' for any rejected value.
   */
  private resolvePostLoginRoute(returnUrl: string | null | undefined): string {
    if (!returnUrl || !returnUrl.startsWith('/') || returnUrl.startsWith('//')) {
      return '/dashboard';
    }
    return returnUrl;
  }

  public setAuthStep(step: AuthStep): void {
    this._authStep.set(step);
  }
}
